// Historical clouds for the timeline: the backend's
// /clouds_full?t=YYYY-MM-DDTHH serves a full-resolution cloud map for any UTC
// hour since the timeline starts (from NOAA's hourly geostationary infrared
// mosaic): raw brightness as a 4096x2048 greyscale JPEG, mapped to grey and
// alpha by recent-clouds.js's curve.

const HOUR_MS = 3_600_000;

// "YYYY-MM-DDTHH" of the UTC hour nearest `timeMs`, or null for live. Never
// later than `nowMs`'s hour, which is the newest the archive can have.
export function cloudHour(timeMs, nowMs = Date.now()) {
  if (timeMs == null) return null;
  const hour = Math.min(Math.round(timeMs / HOUR_MS), Math.floor(nowMs / HOUR_MS));
  return new Date(hour * HOUR_MS).toISOString().slice(0, 13);
}

// Bumped when the API changes how maps are drawn: the images are cached as
// immutable, so a new version needs new URLs (2: smoothed polar caps).
export const CLOUD_IMAGE_VERSION = 2;

export const cloudMapUrl = (backendUrl, hour) => `${backendUrl}/clouds_full?t=${hour}&v=${CLOUD_IMAGE_VERSION}`;

// Loads hour maps on demand, keeping the few most recently used (a 4096x2048
// map is ~21 MB on the GPU with its mipmaps). load(hour) resolves to a
// texture; release runs when one is evicted. A failed load is forgotten so
// it can be retried.
export function createCloudMaps({ load, release = () => {}, keep = 2 }) {
  const cache = new Map(); // hour -> Promise<Texture>, oldest first
  const get = (hour) => {
    let entry = cache.get(hour);
    if (entry) {
      cache.delete(hour);
    } else {
      entry = load(hour);
      entry.catch(() => { if (cache.get(hour) === entry) cache.delete(hour); });
    }
    cache.set(hour, entry);
    while (cache.size > keep) {
      const [oldHour, oldEntry] = cache.entries().next().value;
      cache.delete(oldHour);
      oldEntry.then(release, () => {});
    }
    return entry;
  };
  const clear = () => {
    for (const entry of cache.values()) entry.then(release, () => {});
    cache.clear();
  };
  return { get, clear };
}
